import React from "react";
import { motion } from "framer-motion";
import {
  Clock, Activity as ActivityIcon, Users, Gauge, FolderOpen,
  Download, ArrowUpDown, Flame, TrendingUp,
  Globe, AppWindow, CalendarRange, ChevronDown,
  Brain as BrainIcon, CalendarClock, Mail,
} from "lucide-react";
import { Popover, Tooltip } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DASHBOARD_BASE } from "../../config/featureFlags";
import { useDashTheme } from "./hooks/useDashTheme";
import { useDashboardData } from "./hooks/useDashboardData";
import { SectionCard, Button, Badge, Avatar } from "./components/primitives";
import { DataTable, Pagination, EmptyState } from "./components/DataTable";
import { DateRange } from "./components/DateRange";
import { PageShell } from "./components/PageShell";
import { ToolSelect } from "./components/ToolSelect";
import { DonutChart } from "./components/Charts";
// AskBar/PriorityBoard/ModuleSignals render blocks are commented out in the
// working tree — the imports go with them so the bundle stays honest. Restore
// the import alongside the block if it comes back.
import { StatCard } from "./components/StatCard";
import { IdleLogsModal } from "./components/IdleLogsModal";
import { StealthIdleReasonModal } from "./components/StealthIdleReasonModal";
// Two-tier drill-down (takeaways → full analysis), shared with the Brain view
// so a person/project click behaves identically in both. Dual-source: every
// figure is badged app / agent / both.
import { subjectPath, filterQuery, toolPath, briefPath } from "./components/drilldown/drilldownRoutes";
import { rangeForPreset } from "./components/DateRange";
import { IdleRequestModal } from "./components/IdleRequestModal";
// ADDITIVE: Employee Activity modal — opened from a header button; org-gated + admin-only.
import { EmployeeActivityModal, canUserAccessEmployeeActivity } from "../../modules/employee-activity";
// ADDITIVE 2026-08-16: the redesigned lede (approved mock v5) — one org-scope
// read + deterministic composition; every figure is a door into the drill-down
// PAGES under /classic/*. See DATA_SOURCES_MAP.md for the panel→source matrix.
import { DashAntd } from "./components/DashAntd";
import { ClassicMotion, Shim } from "./components/classic/micro";
import { useClassicOverview } from "./hooks/useClassicOverview";
import {
  LedeCard, KpiGrid, RhythmChart, WeekMatrix, ToolFlow,
  AttentionCard, ProjectsPanel, ToolsList,
} from "./components/classic/panels";
import { InfoDot } from "./components/classic/InfoDot";
import { KnowOrgCard } from "./components/classic/KnowOrgCard";
import { PulseOverview } from "./components/pulse/PulseOverview";
import { PULSE_OVERVIEW } from "../../config/featureFlags";
import { canUserAccessCrm } from "../../modules/crm/utils/orgGating";
import { isS5Organisation } from "../../utils/organization";
// ADDITIVE: isolated Stealth Clock module (self-hiding; org.stealthClockEnabled).
import { ClockCard, ShiftTimelineDrawer, canUserAccessShiftTimeline } from "../../modules/stealth-clock";
// Gmail connections — member list with connected status, opened from the header.
import { GmailModal } from "./components/GmailModal";
import { useOrgProfile } from "./hooks/useOrgProfile";

const fmtHM = (mins) => `${Math.floor((mins || 0) / 60)}h ${Math.round((mins || 0) % 60)}m`;
const fmtDay = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const PERIOD_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
];

function isStealthIdleReasonEnabled() {
  try {
    return window.__orgProfile?.stealthIdleReasonEnabled === true;
  } catch (_) {
    return false;
  }
}

export default function DashboardV2() {
  const t = useDashTheme();
  const d = useDashboardData();
  const { kpi } = d;
  const navigate = useNavigate();
  const [idleTarget, setIdleTarget] = React.useState(null); // { userId, name } | null — regular tracker
  const [stealthIdleTarget, setStealthIdleTarget] = React.useState(null); // opt-in stealth reasons
  const openIdle = React.useCallback((userId, name) => {
    // Feature off → exact previous behavior (regular IdleLogsModal only).
    if (isStealthIdleReasonEnabled()) {
      setStealthIdleTarget({ userId, name });
      return;
    }
    setIdleTarget({ userId, name });
  }, []);
  const fallbackToRegularIdle = React.useCallback(() => {
    setStealthIdleTarget((prev) => {
      if (prev) setIdleTarget(prev);
      return null;
    });
  }, []);

  /* Drill-down is a PAGE, not a drawer. Every one of these is a real
     navigation, so the reader can link to it, refresh it, and use Back —
     none of which a drawer's local state could survive. The dashboard's
     window and member scope ride along in the query string. */
  const goDrill = React.useCallback(
    (subject) => { if (subject?.id) navigate(subjectPath(subject, d.range, d.member)); },
    [navigate, d.range, d.member]
  );
  const openProject = React.useCallback(
    (row) => goDrill({ kind: "project", id: String(row._id), name: row.name }),
    [goDrill]
  );
  const openMember = React.useCallback(
    (row) => goDrill({ kind: "person", id: String(row.userId), name: row.name }),
    [goDrill]
  );

  /* ── the filters live in the address bar ──────────────────────────────────
     Drill-downs are pages now, so the window and member scope have to survive
     leaving this component: a reader who filters to 30 days, opens a member,
     then comes back must land on 30 days, not on today. The same query string
     is what makes a drill-down link mean the same thing to whoever opens it. */
  const [searchParams, setSearchParams] = useSearchParams();
  const hydrated = React.useRef(false);
  const skipFirstSync = React.useRef(null);
  if (skipFirstSync.current === null) {
    skipFirstSync.current = Boolean(
      searchParams.get("start") || searchParams.get("preset") || searchParams.get("member")
    );
  }

  React.useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const start = searchParams.get("start");
    const end = searchParams.get("end");
    const preset = searchParams.get("preset");
    const mem = searchParams.get("member");
    if (start && end) {
      const from = new Date(`${start}T00:00:00`);
      const to = new Date(`${end}T00:00:00`);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        d.setRange({ start: from, end: to, key: preset || "custom" });
      }
    } else if (preset) {
      d.setRange(rangeForPreset(preset));
    }
    if (mem) d.setMember(mem);
  }, [searchParams, d]);

  React.useEffect(() => {
    if (!hydrated.current) return;
    const next = filterQuery(d.range, d.member);
    if (next === searchParams.toString()) return;
    /* The hydration pass owns the first write. Without this the pre-hydration
       range would immediately overwrite the very filters being restored. */
    if (skipFirstSync.current) { skipFirstSync.current = false; return; }
    setSearchParams(next, { replace: true });
  }, [d.range, d.member, searchParams, setSearchParams]);
  // Employee Activity modal — shown only to org admins when the org flag is on.
  // See hooks/useOrgProfile — a bare window read here races Layout's fetch.
  const orgProfile = useOrgProfile();
  const [employeeActivityOpen, setEmployeeActivityOpen] = React.useState(false);
  const showEmployeeActivity = canUserAccessEmployeeActivity(d.user, orgProfile);
  // Shift timeline drawer — ADMIN ONLY (org_admin / main_admin) and only when the org's
  // stealthClockEnabled toggle is on. Employees never see this button; the endpoint
  // enforces the same rules server-side, so hiding it is convenience, not the gate.
  const [gmailOpen, setGmailOpen] = React.useState(false);
  // Hidden entirely when the org has no Gmail data — a dead "0/0" chip is worse
  // than no chip. Still shown while the fetch is in flight so it does not pop in.
  const showGmail = d.gmailLoading || !!d.gmail;
  const gmailConnected = d.gmail?.connectedCount ?? 0;
  const gmailTotal = d.gmail?.total ?? (d.gmail?.members?.length || 0);

  const [shiftTimelineOpen, setShiftTimelineOpen] = React.useState(false);
  const showShiftTimeline = canUserAccessShiftTimeline(d.user, orgProfile);

  // Which insight cards this viewer is allowed to see. A module the user can't
  // open is dropped from the grid entirely — same gates the sidebar uses, so
  // the dashboard never surfaces a card that leads nowhere.
  const isAdmin = d.user?.role === "org_admin" || d.user?.role === "main_admin";
  const moduleAccess = {
    people: isAdmin,
    crm: canUserAccessCrm(d.user, orgProfile),
    email: true,
    goals: isS5Organisation(d.user),
  };
  const moduleRoutes = {
    people: `${DASHBOARD_BASE}/hrms`,
    crm: `${DASHBOARD_BASE}/crm`,
    email: `${DASHBOARD_BASE}/email-insights`,
    goals: `${DASHBOARD_BASE}/goals`,
  };

  // Idle time requests (gated on stealthIdleReasonEnabled)
  // Render-time gate, so it needs the reactive profile too — read straight off
  // `window` this went false on every cold load and hid "Request idle time".
  // The click-time call inside openIdle() is fine on the global: by then the
  // profile has long landed.
  const idleFeatureOn = orgProfile?.stealthIdleReasonEnabled === true;
  const [idleRequestOpen, setIdleRequestOpen] = React.useState(false);

  const memberOptions = [{ value: null, label: "All team members" }, ...d.members];

  // Period control: one button that reads out the active range and opens the
  // presets + calendar on click.
  const [periodOpen, setPeriodOpen] = React.useState(false);
  const periodLabel = PERIOD_PRESETS.find((p) => p.key === d.range.key)?.label
    ?? `${fmtDay(d.range.start)} – ${fmtDay(d.range.end)}`;

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  })();

  /* The redesigned lede reads one org-scope endpoint; composition (headline,
     beats, attention) is deterministic client code over that payload. */
  const classic = useClassicOverview(d.range, d.member);
  const onDrill = goDrill;
  /* Most-visited rows and flow nodes open the TOOL drill-down. Like every
     other drill-down on this page it is a navigation, not an overlay.
     { name, type, category } */
  const onTool = React.useCallback(
    (tool) => { if (tool?.name) navigate(toolPath(tool, d.range, d.member)); },
    [navigate, d.range, d.member]
  );
  /* "Know your organization" — the written brief behind KnowOrgCard. */
  const onKnowOrg = React.useCallback(
    () => navigate(briefPath(d.range, d.member)),
    [navigate, d.range, d.member]
  );

  return (
    <PageShell
      title={`${greeting}${d.user?.firstName ? `, ${d.user.firstName}` : ""}`}
      subtitle={`${new Date(d.range.start).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} – ${new Date(d.range.end).toLocaleDateString("en-US", { month: "long", day: "numeric" })}`}
      actions={
        <div style={{ display: "flex", alignItems: "center", gap: t.s[2] }}>
          {/* The return trip. Brain has had a "Classic" button since it shipped;
              Classic had no way back, so anyone who switched was stuck unless
              they knew the URL. */}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<BrainIcon size={14} />}
            onClick={() => navigate(`${DASHBOARD_BASE}/brain`)}
            title="Company Brain — the org as a live graph"
          >
            Brain
          </Button>
          {showEmployeeActivity && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ActivityIcon size={14} />}
              onClick={() => setEmployeeActivityOpen(true)}
            >
              Employee Activity
            </Button>
          )}
          {showShiftTimeline && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<CalendarClock size={14} />}
              onClick={() => setShiftTimelineOpen(true)}
              title="Shift timeline — clock in/out for the whole team"
            >
              Shift Timeline
            </Button>
          )}
          {showGmail && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Mail size={14} />}
              onClick={() => setGmailOpen(true)}
              title="View Gmail connections"
            >
              {d.gmailLoading ? "Gmail…" : (
                <>
                  <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{gmailConnected}</span>
                  <span style={{ opacity: 0.55 }}>/{gmailTotal}</span> Gmail
                </>
              )}
            </Button>
          )}
        </div>
      }
      /* Minimal ghost toolbar: each control is an icon + its current value, and
         the period picker hides its presets and calendar behind one button. */
      topRight={
        <div className="tg-filterbar">
          <ToolSelect
            icon={<Users size={14} />}
            value={d.member}
            options={memberOptions}
            onChange={d.setMember}
            placeholder="All team members"
          />

          <Popover
            trigger="click"
            open={periodOpen}
            onOpenChange={setPeriodOpen}
            placement="bottomRight"
            arrow={false}
            content={
              <DateRange
                range={d.range} onChange={d.setRange} layoutId="dashRangeChip"
                maxSpanDays={62} maxDate={new Date()} pickerWidth={260}
                presets={PERIOD_PRESETS}
              />
            }
            overlayInnerStyle={{
              padding: t.s[3], background: t.c.surface,
              border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg,
            }}
          >
            <button className={`tg-tool ${periodOpen ? "is-open" : ""}`}>
              <CalendarRange size={14} />
              <span className="tg-tool-value">{periodLabel}</span>
              <ChevronDown size={13} />
            </button>
          </Popover>

          {/* Submitting idle time is open to every role; reviewing them lives
              in the Inbox, which is where the approval queue now sits. */}
          {idleFeatureOn && (
            <button className="tg-tool" onClick={() => setIdleRequestOpen(true)}>
              <Clock size={14} /> Request idle time
            </button>
          )}

        </div>
      }
    >
      {/* Shift clock (ADDITIVE) — org toggle off / non-stealth user ho to khud
          null render karta hai, isliye margin component ke ANDAR pass hota hai
          taaki chhupne par khaali gap na bache. */}
      <ClockCard style={{ marginBottom: t.s[5] }} />

      {/* Team / Projects / Activity are their own pages in the sidebar, so the
          dashboard is the overview only and links out for the full views. */}
      {/* The redesigned overview. The previous OverviewTab is left intact
          below as the fallback, so flipping PULSE_OVERVIEW off restores the
          old page exactly rather than approximately. */}
      {PULSE_OVERVIEW ? (
        <PulseOverview
          d={d} classic={classic} onDrill={onDrill}
          goTeam={() => navigate(`${DASHBOARD_BASE}/team`)}
          goProjects={() => navigate(`${DASHBOARD_BASE}/projects`)}
          goAppUsage={() => navigate(`${DASHBOARD_BASE}/app-usage`)}
          onTool={onTool}
          openMember={openMember}
        />
      ) : (
      <OverviewTab
        t={t} d={d} kpi={kpi} fmtHM={fmtHM} classic={classic} onDrill={onDrill}
        onKnowOrg={onKnowOrg}
        goTeam={() => navigate(`${DASHBOARD_BASE}/team`)}
        goProjects={() => navigate(`${DASHBOARD_BASE}/projects`)}
        openIdle={openIdle} openProject={openProject} openMember={openMember}
        onAsk={(q) => navigate(`${DASHBOARD_BASE}/ask?q=${encodeURIComponent(q)}`)}
        goAppUsage={() => navigate(`${DASHBOARD_BASE}/app-usage`)}
        onTool={onTool}
        moduleAccess={moduleAccess}
        onOpenModule={(k) => navigate(moduleRoutes[k])}
      />
      )}

      {/* Idle logs drill-down (unchanged regular tracker path) */}
      <IdleLogsModal
        isOpen={!!idleTarget}
        onClose={() => setIdleTarget(null)}
        userId={idleTarget?.userId}
        memberName={idleTarget?.name}
      />

      {/* Opt-in stealth-agent idle reasons (only when main-admin org toggle is on) */}
      <StealthIdleReasonModal
        isOpen={!!stealthIdleTarget}
        onClose={() => setStealthIdleTarget(null)}
        userId={stealthIdleTarget?.userId}
        memberName={stealthIdleTarget?.name}
        onFallbackToRegular={fallbackToRegularIdle}
      />

      {/* Employee Activity modal — org admins only, when the org flag is on */}
      {showEmployeeActivity && (
        <EmployeeActivityModal
          open={employeeActivityOpen}
          onClose={() => setEmployeeActivityOpen(false)}
        />
      )}

      {/* Shift timeline drawer — ADMIN ONLY; whole-team clock in/out for a chosen day */}
      {/* Gmail connections modal */}
      {showGmail && (
        <GmailModal open={gmailOpen} onClose={() => setGmailOpen(false)} gmail={d.gmail} />
      )}

      {showShiftTimeline && (
        <ShiftTimelineDrawer
          open={shiftTimelineOpen}
          onClose={() => setShiftTimelineOpen(false)}
        />
      )}

      {/* Idle time request — non-admins only (admins approve, they don't request) */}
      {idleFeatureOn && !isAdmin && (
        <IdleRequestModal isOpen={idleRequestOpen} onClose={() => setIdleRequestOpen(false)} />
      )}

    </PageShell>
  );
}

/* ════════════════════════════ TODAY STRIP ════════════════════════════ */
// The first thing on the page: five live numbers off the same KPI payload the
// charts use, each with one visual (gauge or sparkline) and its own accent, so
// the row reads at a glance instead of as five identical grey boxes.
function TodayStrip({ t, d, kpi, fmtHM }) {
  const series = d.trend.map((x) => x.minutes);
  const latest = series.length ? series[series.length - 1] : 0;
  const prior = series.slice(0, -1);
  const priorAvg = prior.length ? prior.reduce((a, b) => a + b, 0) / prior.length : 0;
  // Percent against the running average of the earlier days in the same series.
  const trackedTrend = priorAvg > 0 ? ((latest - priorAvg) / priorAvg) * 100 : null;

  const teamSize = kpi?.teamSize || 0;
  const activeMembers = kpi?.activeMembers || 0;
  const activeShare = teamSize ? Math.round((activeMembers / teamSize) * 100) : 0;
  const idleShare = kpi?.totalMinutes ? Math.round((kpi.idleMinutes / kpi.totalMinutes) * 100) : 0;
  const goalScore = d.goals?.teamAverageScore;

  return (
    <motion.div
      variants={t.stagger.item}
      style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(196px, 1fr))", gap: t.s[3] }}
    >
      <StatCard
        label="Active now" icon={<Users size={12} />} accent={t.status.positive}
        value={teamSize ? `${activeMembers}/${teamSize}` : String(activeMembers)}
        sublabel={teamSize ? "members tracking" : "members"}
        gauge={activeShare} gaugeColor={t.status.positive} loading={d.kpiLoading}
      />
      <StatCard
        label="Tracked" icon={<Clock size={12} />} accent={t.c.primary}
        value={fmtHM(kpi?.totalMinutes || 0)}
        trend={trackedTrend} trendLabel="vs period avg"
        spark={series.length > 1 ? series : undefined} sparkColor={t.c.primary}
        loading={d.kpiLoading}
      />
      <StatCard
        label="Productivity" icon={<Gauge size={12} />} accent={t.c.ai}
        value={`${kpi?.utilization || 0}%`} sublabel="active share of tracked time"
        gauge={kpi?.utilization || 0} gaugeColor={t.c.ai} loading={d.kpiLoading}
      />
      <StatCard
        label="Idle" icon={<Flame size={12} />} accent={t.status.warning}
        value={fmtHM(kpi?.idleMinutes || 0)} sublabel={`${idleShare}% of tracked time`}
        gauge={idleShare} gaugeColor={t.status.warning} loading={d.kpiLoading}
      />
      {goalScore != null && (
        <StatCard
          label="Goal score" icon={<TrendingUp size={12} />} accent={t.accents[4]}
          value={`${Math.round(goalScore)}%`}
          sublabel={`${d.goals.completedGoalsCount}/${d.goals.totalGoalsCount} goals done`}
          trend={d.goals.previousMonthChange} trendLabel="vs last month"
          gauge={goalScore} gaugeColor={t.accents[4]}
        />
      )}
    </motion.div>
  );
}

/* ════════════════════════════ OVERVIEW TAB ════════════════════════════ */
function OverviewTab({ t, d, kpi, fmtHM, classic, onDrill, onTool, onKnowOrg, goTeam, goProjects, goAppUsage, openIdle, openProject, openMember, onAsk, moduleAccess, onOpenModule }) {
  // Active vs idle donut (parity — kept from the previous layout)
  const activeIdle = kpi ? [
    { label: "Active", value: kpi.activeMinutes, color: t.status.positive },
    { label: "Idle", value: kpi.idleMinutes, color: t.status.neutral },
  ] : [];

  /* Members-per-project, joined from the parity rows the page already loads. */
  const projMembersById = React.useMemo(() => {
    const m = new Map();
    for (const r of d.projectRows || []) {
      if (r && r._id != null && r.members != null) m.set(String(r._id), r.members);
    }
    return m;
  }, [d.projectRows]);

  return (
    <DashAntd>
    <motion.div variants={t.stagger.container} initial="initial" animate="animate" style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
      <ClassicMotion />

      {/* ═══ Row 1: the lede — headline with proofs beside the KPI grid.
          Replaces TodayStrip; every StatCard it carried lives on as a KPI cell
          (Tracked / Active / People / Productivity / Goal score) with the chart
          its metric deserves. `classic.error` falls back to the old strip so a
          failed overview read can never blank the top of the dashboard. ═══ */}
      {classic.error ? (
        <TodayStrip t={t} d={d} kpi={kpi} fmtHM={fmtHM} />
      ) : (
        <motion.div variants={t.stagger.item} className="dashv2-lede-row"
          style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)", gap: t.s[4], alignItems: "stretch" }}>
          <LedeCard headline={classic.headline} story={classic.story} overview={classic.overview}
            loading={classic.loading} onDrill={onDrill} onTeam={goTeam} />
          <KpiGrid overview={classic.overview} kpi={kpi} goals={d.goals} loading={classic.loading} onTeam={goTeam} />
        </motion.div>
      )}

      {/* Row 2: what to act on (ranked) beside the way to interrogate it. They
          share a row because "here's what's wrong" and "ask why" are one move. */}
      {/* <motion.div
        variants={t.stagger.item}
        className="dashv2-focus-row"
        style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.55fr) minmax(0, 1fr)", gap: t.s[4], alignItems: "stretch" }}
      >
        <PriorityBoard onOpen={() => {}} />
        <AskBar onAsk={onAsk} />
      </motion.div> */}

      {/* Row 4: per-module signals, hidden for modules this viewer can't open */}
      {/* <ModuleSignals access={moduleAccess} onOpen={onOpenModule} /> */}

      {/* ═══ Row 5: rhythm (with last-period ghosts) beside the right rail:
          focus (DETAIL, self-hiding), attention, then the parity donut. ═══ */}
      <motion.div variants={t.stagger.item} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)", gap: t.s[4], alignItems: "start" }} className="dashv2-charts-row">
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
          <RhythmChart overview={classic.overview} loading={classic.loading} />
          <WeekMatrix overview={classic.overview} loading={classic.loading} />
          <ToolFlow overview={classic.overview} loading={classic.loading} onOpenAppUsage={goAppUsage} onTool={onTool} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
          {/* FocusCard removed 2026-08-16 (user decision): avg-switch-gap scored
              ~5/100 every period from partial coverage — permanent alarm, no
              action. Component kept in panels.jsx if ever wanted back. */}
          <KnowOrgCard onOpen={onKnowOrg} />
          <AttentionCard attention={classic.attention} loading={classic.loading} onDrill={onDrill} />
          <SectionCard accent="blueGreen" title="Time split" subtitle="Active vs idle" icon={<Gauge size={16} />}>
            <DonutChart
              data={activeIdle} centerLabel={kpi ? `${kpi.utilization}%` : "—"} centerSub="active"
              formatValue={(v) => fmtHM(v)} loading={d.kpiLoading}
            />
          </SectionCard>
        </div>
      </motion.div>

      {/* Row 6: Top team + Top projects (compact previews with "view all").
          Team performance gets a bit more width than projects. */}
      <motion.div variants={t.stagger.item} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr)", gap: t.s[4] }} className="dashv2-preview-row">
        <PreviewCard t={t} accent="indigo" title="Team performance" icon={<Users size={16} />} onViewAll={goTeam}>
          <TeamSection t={t} d={d} preview openIdle={openIdle} openMember={openMember} />
        </PreviewCard>
        <PreviewCard t={t} accent="orange" title="Most active projects" icon={<FolderOpen size={16} />} onViewAll={goProjects}>
          {/* Graphical rows sharing the share bar's palette; the parity table
              remains the fallback whenever the overview read is unavailable. */}
          {classic.loading && classic.overview?.projects?.length ? (
            <div style={{ padding: 16 }}>
              <Shim h={10} r={999} />
              <Shim h={110} style={{ marginTop: 14 }} />
            </div>
          ) : classic.overview?.projects?.length ? (
            <ProjectsPanel
              overview={classic.overview}
              membersById={projMembersById}
              onDrill={onDrill}
            />
          ) : (
            <ProjectSection t={t} d={d} preview openProject={openProject} />
          )}
        </PreviewCard>
      </motion.div>

      {/* Row 7: Most visited apps/websites (click-through → App usage) */}
      <motion.div variants={t.stagger.item}>
        <SectionCard accent="aqua" title="Most visited" subtitle="Apps & websites by time · click a row for the full breakdown" icon={<Globe size={16} />} padding={0}
          actions={<><Button variant="ghost" size="sm" onClick={goAppUsage} rightIcon={<ArrowUpDown size={13} style={{ transform: "rotate(90deg)" }} />}>App usage</Button><InfoDot metric="tools" /></>}>
          {/* Iconic rows (brand monograms / favicons + spark + share bar);
              the DataTable stays for the loading and empty states. */}
          {!d.mostVisitedLoading && d.mostVisited?.length
            ? <ToolsList d={d} overview={classic.overview} onTool={onTool} />
            : <MostVisitedList t={t} d={d} preview onRow={(r) => onTool({ name: r.name, type: r.type, category: r.category })} />}
        </SectionCard>
      </motion.div>

    </motion.div>
    </DashAntd>
  );
}

function PreviewCard({ t, title, icon, accent, onViewAll, children }) {
  return (
    <SectionCard
      title={title} icon={icon} accent={accent} padding={0}
      actions={<Button variant="ghost" size="sm" onClick={onViewAll} rightIcon={<ArrowUpDown size={13} style={{ transform: "rotate(90deg)" }} />}>View all</Button>}
    >
      {children}
    </SectionCard>
  );
}

/* ════════════════════════════ TEAM SECTION ════════════════════════════ */
function TeamSection({ t, d, full, preview, openIdle, openMember }) {
  const rows = preview ? d.teamRows.slice(0, 5) : d.teamRows;
  const table = (
    <DataTable
      loading={d.teamLoading} rows={rows} rowKey={(r) => r.userId}
      sortKey={d.teamSort.key} sortDir={d.teamSort.dir}
      // TEMPORARILY DISABLED — member drill-down drawer, to be re-enabled later.
      // Restore by uncommenting the onRowClick line below. openMember (~line 63)
      // sets `drawer` to member mode, which DetailDrawer renders. DataTable
      // guards this prop, so with it off no row handler is attached and the
      // cursor stays `default`. The Idle pill (IdleCell) keeps its own onClick
      // and is unaffected.
      // onRowClick={openMember}
      onSort={(k) => d.setTeamSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }))}
      emptyState={<EmptyState icon={<Users size={22} />} title="No team activity" description="No members tracked time in this period." />}
      columns={[
        { key: "name", header: "Member", render: (r) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar user={r} name={r.name} size={32} highlight={r.isCurrentUser} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                {r.name}{r.isCurrentUser && <Badge tone="brand">You</Badge>}
                {r.badgeIcon && <img src={r.badgeIcon} alt={r.badge || "rank"} title={r.badge || "Rank"} style={{ width: 16, height: 16, objectFit: "contain" }} />}
                {r.streak > 2 && <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: t.status.warning, fontSize: t.font.size.xs }}><Flame size={12} />{r.streak}</span>}
              </div>
              <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{r.position}</div>
            </div>
          </div>
        ) },
        { key: "hours", header: "Tracked", align: "right", width: 100, sortable: true, render: (r) => <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.hours}</span> },
        { key: "activeTime", header: "Active", align: "right", width: 95, render: (r) => <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{r.activeTime}</span> },
        { key: "idleTime", header: "Idle", align: "right", width: 100, render: (r) => <IdleCell t={t} r={r} onClick={openIdle} /> },
        /* One length, two meanings. The Active and Idle columns above give the
           figures; this gives the ratio without the reader dividing them in
           their head across a dozen rows. Reads straight off activeMinutes /
           idleMinutes, which the row already carries as raw numbers.
           (The mock also showed a first→last "working window" bar. Not built:
           the team payload carries lastActivity but no firstActivity, and a
           start time is not something to invent.) */
        { key: "split", header: "Split", width: 116, render: (r) => {
          const tot = (r.activeMinutes || 0) + (r.idleMinutes || 0);
          if (!tot) return <span style={{ fontSize: 11, color: t.c.textTertiary }}>no data</span>;
          const a = Math.round((r.activeMinutes / tot) * 100);
          return (
            <Tooltip title={`${a}% active · ${100 - a}% idle`}>
              <span style={{ display: "flex", height: 8, width: 96, borderRadius: 999, overflow: "hidden", background: t.c.surfaceMuted }}>
                <i style={{ width: `${a}%`, background: t.status.positive }} />
                <i style={{ width: `${100 - a}%`, background: t.status.warning }} />
              </span>
            </Tooltip>
          );
        } },
        ...(full ? [{ key: "sessions", header: "Sessions", align: "right", width: 95, render: (r) => <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{r.sessions}</span> }] : []),
      ]}
    />
  );

  if (preview) return table;
  return (
    <SectionCard
      title="Team performance" subtitle={d.teamMeta.total ? `${d.teamMeta.total} members` : undefined} icon={<Users size={16} />} padding={0}
      actions={<Button variant="secondary" size="sm" leftIcon={<Download size={14} />} onClick={d.exportTeam}>Export</Button>}
    >
      {table}
      <Pagination page={d.teamMeta.page} pages={d.teamMeta.pages} total={d.teamMeta.total} pageLabel="members" onChange={d.setTeamPage} />
    </SectionCard>
  );
}

/* ════════════════════════════ PROJECT SECTION ════════════════════════════ */
function ProjectSection({ t, d, full, preview, openProject }) {
  const rows = preview ? d.projectRows.slice(0, 5) : d.projectRows;
  const table = (
    <DataTable
      loading={d.projectLoading} rows={rows} rowKey={(r) => r._id}
      sortKey={d.projectSort.key} sortDir={d.projectSort.dir}
      // TEMPORARILY DISABLED — project drill-down drawer, to be re-enabled later.
      // Restore by uncommenting the onRowClick line below. openProject (~line 62)
      // sets `drawer` to project mode, which DetailDrawer renders. Disabled
      // alongside the member row-click in TeamSection (~line 432).
      // onRowClick={openProject}
      onSort={(k) => d.setProjectSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }))}
      emptyState={<EmptyState icon={<FolderOpen size={22} />} title="No project activity" description="No tracked time for projects in this period." />}
      columns={[
        { key: "name", header: "Project", render: (r) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: 99, background: r.isActive ? t.status.positive : t.status.neutral, flexShrink: 0 }} />
            <span style={{ fontWeight: 500 }}>{r.name}</span>
          </div>
        ) },
        ...(full ? [{ key: "status", header: "Status", width: 100, render: (r) => <Badge tone={r.isActive ? "positive" : "neutral"} dot>{r.isActive ? "Active" : "Idle"}</Badge> }] : []),
        { key: "members", header: "Members", align: "right", width: 90, render: (r) => <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{r.members}</span> },
        ...(full ? [{ key: "avgSession", header: "Avg session", align: "right", width: 120, render: (r) => <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{r.avgSession}</span> }] : []),
        { key: "time", header: "Time spent", align: "right", width: 120, sortable: true, render: (r) => <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.timeSpent}</span> },
      ]}
    />
  );

  if (preview) return table;
  return (
    <SectionCard
      title="Project overview" subtitle={d.projectMeta.total ? `${d.projectMeta.total} projects` : undefined} icon={<FolderOpen size={16} />} padding={0}
    >
      {table}
      <Pagination page={d.projectMeta.page} pages={d.projectMeta.pages} total={d.projectMeta.total} pageLabel="projects" onChange={d.setProjectPage} />
    </SectionCard>
  );
}

/* ════════════════════════════ MOST VISITED ════════════════════════════ */
function MostVisitedList({ t, d, preview, onRow }) {
  const rows = preview ? d.mostVisited.slice(0, 6) : d.mostVisited;
  return (
    <DataTable
      loading={d.mostVisitedLoading} rows={rows} rowKey={(r, i) => `${r.name}-${i}`}
      onRowClick={onRow}
      emptyState={<EmptyState icon={<Globe size={22} />} title="No activity captured" description="App & website usage will appear here once screenshots are processed." />}
      columns={[
        { key: "name", header: "App / Website", render: (r) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: t.c.surfaceMuted, color: t.c.textSecondary, flexShrink: 0 }}>
              {r.type === "website" ? <Globe size={15} /> : <AppWindow size={15} />}
            </span>
            <span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
          </div>
        ) },
        { key: "category", header: "Type / Category", width: 180, render: (r) => (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Badge tone="neutral">{r.type === "website" ? "Website" : "App"}</Badge>
            {r.category && <Badge tone="info">{r.category}</Badge>}
          </div>
        ) },
        ...(preview ? [] : [{ key: "sessions", header: "Sessions", align: "right", width: 100, render: (r) => <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{r.sessions}</span> }]),
        { key: "time", header: "Time", align: "right", width: 110, render: (r) => <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.time}</span> },
      ]}
    />
  );
}

/* ──────────── shared ──────────── */
// Clickable idle-time cell → opens the idle logs drill-down modal.
function IdleCell({ t, r, onClick }) {
  const hasIdle = r.idleTime && r.idleTime !== "0h 0m";
  if (!onClick || !hasIdle) {
    return <span style={{ color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>{r.idleTime || "0h 0m"}</span>;
  }
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(r.userId, r.name); }}
      title="View idle logs"
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 99,
        border: `1px solid ${t.status.warningSoft}`, background: t.status.warningSoft, color: t.status.warning,
        fontSize: t.font.size.sm, fontWeight: 600, cursor: "pointer", fontVariantNumeric: "tabular-nums",
        fontFamily: t.font.family, transition: "filter 140ms",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(0.94)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
    >
      {r.idleTime}
    </button>
  );
}

